import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, Plus, Edit2, Trash2, Users, DollarSign, Percent, Check, Upload, FileText, Link, Image, Calendar, CreditCard, RefreshCw, Receipt, MessageSquare, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { portfolioService, api } from '@/lib/api';
import CampaignManager from './CampaignManager';

const PORTFOLIO_TYPES = [
  { value: 'efectivo', label: 'Efectivo', description: 'Sin documento adjunto' },
  { value: 'servicio', label: 'Servicio', description: 'Muestra Factura al cliente' },
  { value: 'multas', label: 'Multas', description: 'Muestra Acta al cliente' }
];

const LINK_FIELDS = [
  { value: 'dni', label: 'DNI' },
  { value: 'cuit', label: 'CUIT' },
  { value: 'client_code', label: 'Código de Cliente' },
  { value: 'plate', label: 'Dominio' }
];

const PortfolioManager = ({ onPortfolioSelect, selectedPortfolioId }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showCampaignsModal, setShowCampaignsModal] = useState(null); // portfolio id
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  
  const logoInputRef = useRef(null);
  const docInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    portfolio_type: 'efectivo',
    charge_fees: false,
    fees_percentage: '',
    fees_include_vat: false,
    vat_percentage: '21',
    document_link_field: 'dni',
    google_drive_folder_id: '',
    google_drive_use_oauth: false,
    // Transfer fees
    charge_transfer_fee: false,
    transfer_fee_type: 'fixed',
    transfer_fee_amount: '',
    transfer_fee_applies_to: 'capital',
    // Refinancing
    enable_refinancing: false,
    refinancing_policy_urls: [],
    // Partial acta payment
    allow_partial_acta_payment: false,
    // Payment plans
    enable_payment_plans: false,
    max_installments: '6',
    installment_interest_rate: '0',
    // Payment methods override
    override_payment_methods: false,
    payment_methods: {
      mercadopago: true,
      transfer: true,
      modo: false,
      pagofacil: false,
      rapipago: false
    },
    // Reminders
    enable_reminders: false,
    reminder_config: {
      message_template: 'Hola {nombre}, te recordamos que tienes una deuda de {deuda}. Puedes consultar y pagar en: {link}'
    },
    // Document Request (solicitud de actas/facturas)
    enable_document_requests: false,
    document_request_config: {
      responsible_email: '',
      responsible_phone: '',
      notify_whatsapp: false,
      response_time_hours: 48
    }
  });

  useEffect(() => {
    loadPortfolios();
  }, []);

  const loadPortfolios = async () => {
    try {
      const response = await portfolioService.list();
      setPortfolios(response.data);
    } catch (error) {
      toast.error('Error al cargar carteras');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo_url: '',
      portfolio_type: 'efectivo',
      charge_fees: false,
      fees_percentage: '',
      fees_include_vat: false,
      vat_percentage: '21',
      document_link_field: 'dni',
      google_drive_folder_id: '',
      google_drive_use_oauth: false,
      // Transfer fees
      charge_transfer_fee: false,
      transfer_fee_type: 'fixed',
      transfer_fee_amount: '',
      transfer_fee_applies_to: 'capital',
      // Refinancing
      enable_refinancing: false,
      refinancing_policy_urls: [],
      // Partial acta payment
      allow_partial_acta_payment: false,
      enable_payment_plans: false,
      max_installments: '6',
      installment_interest_rate: '0',
      // Payment methods override
      override_payment_methods: false,
      payment_methods: {
        mercadopago: true,
        transfer: true,
        modo: false,
        pagofacil: false,
        rapipago: false
      },
      // Reminders
      enable_reminders: false,
      reminder_config: {
        message_template: 'Hola {nombre}, te recordamos que tienes una deuda de {deuda}. Puedes consultar y pagar en: {link}'
      },
      enable_document_requests: false,
      document_request_config: {
        responsible_email: '',
        responsible_phone: '',
        notify_whatsapp: false,
        response_time_hours: 48
      }
    });
  };

  const handleLogoUpload = async (e, portfolioId = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      if (portfolioId) {
        const response = await api.post(`/company/portfolios/${portfolioId}/upload-logo`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Logo actualizado');
        loadPortfolios();
      } else {
        // For new portfolio, convert to base64 and store in formData
        const reader = new FileReader();
        reader.onload = (event) => {
          setFormData({ ...formData, logo_url: event.target.result });
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      toast.error('Error al subir logo');
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        logo_url: formData.logo_url || null,
        portfolio_type: formData.portfolio_type,
        charge_fees: formData.charge_fees,
        fees_percentage: formData.charge_fees ? parseFloat(formData.fees_percentage) || 0 : null,
        fees_include_vat: formData.fees_include_vat,
        vat_percentage: parseFloat(formData.vat_percentage) || 21,
        document_link_field: formData.portfolio_type !== 'efectivo' ? formData.document_link_field : null,
        google_drive_folder_id: formData.google_drive_folder_id || null,
        google_drive_use_oauth: formData.google_drive_use_oauth,
        // Transfer fees
        charge_transfer_fee: formData.charge_transfer_fee,
        transfer_fee_type: formData.transfer_fee_type,
        transfer_fee_amount: formData.charge_transfer_fee ? parseFloat(formData.transfer_fee_amount) || 0 : 0,
        transfer_fee_applies_to: formData.transfer_fee_applies_to,
        // Refinancing
        enable_refinancing: formData.enable_refinancing,
        allow_partial_acta_payment: formData.allow_partial_acta_payment,
        refinancing_policy_urls: formData.refinancing_policy_urls || [],
        // Payment plans
        enable_payment_plans: formData.enable_payment_plans,
        max_installments: parseInt(formData.max_installments) || 6,
        installment_interest_rate: parseFloat(formData.installment_interest_rate) || 0,
        // Payment methods override
        override_payment_methods: formData.override_payment_methods,
        payment_methods: formData.override_payment_methods ? formData.payment_methods : null,
        // Reminders
        enable_reminders: formData.enable_reminders,
        reminder_config: formData.enable_reminders ? formData.reminder_config : null,
        // Document requests
        enable_document_requests: formData.enable_document_requests,
        document_request_config: formData.enable_document_requests ? formData.document_request_config : null
      };

      await portfolioService.create(payload);
      toast.success('Cartera creada exitosamente');
      setShowCreateModal(false);
      resetForm();
      loadPortfolios();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al crear cartera';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (portfolio) => {
    setEditingPortfolio(portfolio);
    setFormData({
      name: portfolio.name,
      logo_url: portfolio.logo_url || '',
      portfolio_type: portfolio.portfolio_type || 'efectivo',
      charge_fees: portfolio.charge_fees,
      fees_percentage: portfolio.fees_percentage?.toString() || '',
      fees_include_vat: portfolio.fees_include_vat,
      vat_percentage: portfolio.vat_percentage?.toString() || '21',
      document_link_field: portfolio.document_link_field || 'dni',
      google_drive_folder_id: portfolio.google_drive_folder_id || '',
      google_drive_use_oauth: portfolio.google_drive_use_oauth || false,
      // Transfer fees
      charge_transfer_fee: portfolio.charge_transfer_fee || false,
      transfer_fee_type: portfolio.transfer_fee_type || 'fixed',
      transfer_fee_amount: portfolio.transfer_fee_amount?.toString() || '',
      transfer_fee_applies_to: portfolio.transfer_fee_applies_to || 'capital',
      // Refinancing
      enable_refinancing: portfolio.enable_refinancing || false,
      allow_partial_acta_payment: portfolio.allow_partial_acta_payment || false,
      refinancing_policy_urls: portfolio.refinancing_policy_urls || [],
      // Payment plans
      enable_payment_plans: portfolio.enable_payment_plans || false,
      max_installments: portfolio.max_installments?.toString() || '6',
      installment_interest_rate: portfolio.installment_interest_rate?.toString() || '0',
      // Payment methods override
      override_payment_methods: portfolio.override_payment_methods || false,
      payment_methods: portfolio.payment_methods || {
        mercadopago: true,
        transfer: true,
        modo: false,
        pagofacil: false,
        rapipago: false
      },
      // Reminders
      enable_reminders: portfolio.enable_reminders || false,
      reminder_config: portfolio.reminder_config || {
        message_template: 'Hola {nombre}, te recordamos que tienes una deuda de {deuda}. Puedes consultar y pagar en: {link}'
      },
      enable_document_requests: portfolio.enable_document_requests || false,
      document_request_config: portfolio.document_request_config || {
        responsible_email: '',
        responsible_phone: '',
        notify_whatsapp: false,
        response_time_hours: 48
      }
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        logo_url: formData.logo_url || null,
        portfolio_type: formData.portfolio_type,
        charge_fees: formData.charge_fees,
        fees_percentage: formData.charge_fees ? parseFloat(formData.fees_percentage) || 0 : null,
        fees_include_vat: formData.fees_include_vat,
        vat_percentage: parseFloat(formData.vat_percentage) || 21,
        document_link_field: formData.portfolio_type !== 'efectivo' ? formData.document_link_field : null,
        google_drive_folder_id: formData.google_drive_folder_id || null,
        google_drive_use_oauth: formData.google_drive_use_oauth,
        // Transfer fees
        charge_transfer_fee: formData.charge_transfer_fee,
        transfer_fee_type: formData.transfer_fee_type,
        transfer_fee_amount: formData.charge_transfer_fee ? parseFloat(formData.transfer_fee_amount) || 0 : 0,
        transfer_fee_applies_to: formData.transfer_fee_applies_to,
        // Refinancing
        enable_refinancing: formData.enable_refinancing,
        allow_partial_acta_payment: formData.allow_partial_acta_payment,
        refinancing_policy_urls: formData.refinancing_policy_urls || [],
        // Payment plans
        enable_payment_plans: formData.enable_payment_plans,
        max_installments: parseInt(formData.max_installments) || 6,
        installment_interest_rate: parseFloat(formData.installment_interest_rate) || 0,
        // Payment methods override
        override_payment_methods: formData.override_payment_methods,
        payment_methods: formData.override_payment_methods ? formData.payment_methods : null,
        // Reminders
        enable_reminders: formData.enable_reminders,
        reminder_config: formData.enable_reminders ? formData.reminder_config : null,
        // Document requests
        enable_document_requests: formData.enable_document_requests,
        document_request_config: formData.enable_document_requests ? formData.document_request_config : null
      };

      await portfolioService.update(editingPortfolio.id, payload);
      toast.success('Cartera actualizada exitosamente');
      setShowEditModal(false);
      setEditingPortfolio(null);
      resetForm();
      loadPortfolios();
    } catch (error) {
      toast.error('Error al actualizar cartera');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (portfolio) => {
    if (portfolio.debtors_count > 0) {
      toast.error(`No se puede eliminar. La cartera tiene ${portfolio.debtors_count} clientes.`);
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar la cartera "${portfolio.name}"?`)) {
      return;
    }

    try {
      await portfolioService.delete(portfolio.id);
      toast.success('Cartera eliminada');
      loadPortfolios();
      if (selectedPortfolioId === portfolio.id) {
        onPortfolioSelect(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cartera');
    }
  };

  // Document management functions
  const openDocumentsModal = async (portfolio) => {
    setEditingPortfolio(portfolio);
    setShowDocumentsModal(true);
    await loadDocuments(portfolio.id);
  };

  const loadDocuments = async (portfolioId) => {
    try {
      const response = await api.get(`/company/portfolios/${portfolioId}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Error al cargar documentos');
    }
  };

  const handleDocumentsUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingDocs(true);
    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await api.post(
        `/company/portfolios/${editingPortfolio.id}/documents/upload`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.errors_count > 0) {
        toast.warning(`${response.data.uploaded} documentos subidos, ${response.data.errors_count} errores`);
      } else {
        toast.success(`${response.data.uploaded} documentos subidos`);
      }
      
      await loadDocuments(editingPortfolio.id);
    } catch (error) {
      toast.error('Error al subir documentos');
    } finally {
      setUploadingDocs(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (linkValue) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    
    try {
      await api.delete(`/company/portfolios/${editingPortfolio.id}/documents/${linkValue}`);
      toast.success('Documento eliminado');
      await loadDocuments(editingPortfolio.id);
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  const getTypeBadge = (type) => {
    const config = {
      efectivo: { label: 'Efectivo', className: 'bg-slate-100 text-slate-700' },
      servicio: { label: 'Servicio', className: 'bg-blue-100 text-blue-700' },
      multas: { label: 'Multas', className: 'bg-amber-100 text-amber-700' }
    };
    const { label, className } = config[type] || config.efectivo;
    return <Badge className={className}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={`skeleton-${i}`} className="h-40 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Hidden file inputs */}
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleLogoUpload(e, editingPortfolio?.id)}
      />
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Carteras ({portfolios.length})
          </h2>
          <p className="text-sm text-slate-600">
            Gestiona tus carteras de clientes y configura honorarios
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-portfolio-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nueva Cartera
        </Button>
      </div>

      {/* Portfolio Grid */}
      {portfolios.length === 0 ? (
        <Card className="p-12 border-slate-200 bg-white text-center">
          <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Sin carteras</h3>
          <p className="text-slate-600 mb-4">Crea tu primera cartera para organizar clientes</p>
          <Button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Crear Primera Cartera
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* "Todos los deudores" card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card
              className={`p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
                selectedPortfolioId === null
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              onClick={() => onPortfolioSelect(null)}
              data-testid="all-debtors-card"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
                {selectedPortfolioId === null && (
                  <Badge className="bg-teal-100 text-teal-700">
                    <Check className="w-3 h-3 mr-1" /> Seleccionado
                  </Badge>
                )}
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Todos los Clientes</h3>
              <p className="text-sm text-slate-600">Ver clientes de todas las carteras</p>
            </Card>
          </motion.div>

          {/* Portfolio cards */}
          {portfolios.map((portfolio, index) => (
            <motion.div
              key={portfolio.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card
                className={`p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
                  selectedPortfolioId === portfolio.id
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                onClick={() => onPortfolioSelect(portfolio.id)}
                data-testid={`portfolio-card-${portfolio.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  {portfolio.logo_url ? (
                    <img
                      src={portfolio.logo_url}
                      alt={portfolio.name}
                      className="w-12 h-12 rounded-lg object-contain"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                      <FolderOpen className="w-6 h-6 text-teal-600" />
                    </div>
                  )}
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setShowCampaignsModal(portfolio); }}
                      title="Campañas"
                      className="text-violet-600 hover:bg-violet-50"
                      data-testid={`campaigns-portfolio-${portfolio.id}`}
                    >
                      <Calendar className="w-4 h-4" />
                    </Button>
                    {portfolio.portfolio_type !== 'efectivo' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openDocumentsModal(portfolio); }}
                        title="Documentos"
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); handleEdit(portfolio); }}
                      data-testid={`edit-portfolio-${portfolio.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); handleDelete(portfolio); }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-portfolio-${portfolio.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <h3 className="font-semibold text-slate-900 mb-2">{portfolio.name}</h3>

                <div className="flex items-center gap-2 mb-3">
                  {getTypeBadge(portfolio.portfolio_type)}
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {portfolio.debtors_count || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      ${(portfolio.total_debt || 0).toLocaleString()}
                    </span>
                  </div>
                </div>

                {portfolio.charge_fees && (
                  <Badge className="bg-amber-100 text-amber-700">
                    <Percent className="w-3 h-3 mr-1" />
                    {portfolio.fees_percentage}% honorarios
                    {portfolio.fees_include_vat && ' + IVA'}
                  </Badge>
                )}

                {selectedPortfolioId === portfolio.id && (
                  <div className="mt-3 pt-3 border-t border-teal-200">
                    <Badge className="bg-teal-100 text-teal-700">
                      <Check className="w-3 h-3 mr-1" /> Filtro activo
                    </Badge>
                  </div>
                )}
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva Cartera</DialogTitle>
            <DialogDescription>
              Crea una cartera para agrupar clientes de un tipo específico
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="create-name">Nombre del Cliente/Cartera *</Label>
              <Input
                id="create-name"
                data-testid="portfolio-name-input"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Banco XYZ, Retail ABC"
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Logo de la Cartera</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="URL del logo"
                    className="flex-1"
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Tipo de Cartera</Label>
                <Select
                  value={formData.portfolio_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, portfolio_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="servicio">Servicio (Facturas)</SelectItem>
                    <SelectItem value="multas">Multas (Actas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm">Cobrar honorarios</p>
                <p className="text-xs text-slate-500">Agregar porcentaje adicional a la deuda</p>
              </div>
              <Switch
                checked={formData.charge_fees}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, charge_fees: checked }))}
              />
            </div>

            {formData.charge_fees && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Porcentaje de Honorarios</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={formData.fees_percentage}
                      onChange={(e) => setFormData(prev => ({ ...prev, fees_percentage: e.target.value }))}
                      placeholder="15"
                      autoComplete="off"
                    />
                    <Percent className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.fees_include_vat}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, fees_include_vat: checked }))}
                  />
                  <Label className="text-sm">Incluir IVA ({formData.vat_percentage}%)</Label>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={submitting || !formData.name.trim()}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="create-portfolio-submit"
              >
                {submitting ? 'Creando...' : 'Crear Cartera'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cartera</DialogTitle>
            <DialogDescription>
              Modifica la configuración de la cartera
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Nombre del Cliente/Cartera *</Label>
              <Input
                id="edit-name"
                data-testid="portfolio-name-input"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Banco XYZ, Retail ABC"
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Logo de la Cartera</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="URL del logo"
                    className="flex-1"
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Tipo de Cartera</Label>
                <Select
                  value={formData.portfolio_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, portfolio_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="servicio">Servicio (Facturas)</SelectItem>
                    <SelectItem value="multas">Multas (Actas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.portfolio_type !== 'efectivo' && (
              <>
                <div>
                  <Label>Campo de Vinculación</Label>
                  <Select
                    value={formData.document_link_field}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, document_link_field: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINK_FIELDS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    El nombre del documento debe coincidir con este campo del cliente
                  </p>
                </div>

                <div className="space-y-3 p-3 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Google Drive
                      </p>
                      <p className="text-xs text-slate-500">Vincular documentos desde Google Drive</p>
                    </div>
                    <Switch
                      checked={formData.google_drive_use_oauth}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, google_drive_use_oauth: checked }))}
                    />
                  </div>

                  {!formData.google_drive_use_oauth && (
                    <div>
                      <Label className="flex items-center gap-1">
                        <Link className="w-3 h-3" />
                        ID de Carpeta de Google Drive
                      </Label>
                      <Input
                        value={formData.google_drive_folder_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, google_drive_folder_id: e.target.value }))}
                        placeholder="1ABC...xyz (ID de la carpeta compartida)"
                        autoComplete="off"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        La carpeta debe ser pública o compartida con el servicio
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm">Cobrar honorarios</p>
                <p className="text-xs text-slate-500">Agregar porcentaje adicional a la deuda</p>
              </div>
              <Switch
                checked={formData.charge_fees}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, charge_fees: checked }))}
              />
            </div>

            {formData.charge_fees && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Porcentaje de Honorarios</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={formData.fees_percentage}
                      onChange={(e) => setFormData(prev => ({ ...prev, fees_percentage: e.target.value }))}
                      placeholder="15"
                      autoComplete="off"
                    />
                    <Percent className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.fees_include_vat}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, fees_include_vat: checked }))}
                  />
                  <Label className="text-sm">Incluir IVA ({formData.vat_percentage}%)</Label>
                </div>
              </div>
            )}

            {/* Transfer Fees Section */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-amber-600" />
                  Cobrar cargo por transferencia
                </p>
                <p className="text-xs text-slate-500">Agregar un cargo oculto al total</p>
              </div>
              <Switch
                checked={formData.charge_transfer_fee}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, charge_transfer_fee: checked }))}
              />
            </div>

            {formData.charge_transfer_fee && (
              <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo de cargo</Label>
                    <Select
                      value={formData.transfer_fee_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, transfer_fee_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Monto fijo</SelectItem>
                        <SelectItem value="percentage">Porcentaje</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{formData.transfer_fee_type === 'fixed' ? 'Monto ($)' : 'Porcentaje (%)'}</Label>
                    <Input
                      type="number"
                      value={formData.transfer_fee_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, transfer_fee_amount: e.target.value }))}
                      placeholder={formData.transfer_fee_type === 'fixed' ? '100' : '2.5'}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div>
                  <Label>Aplicar sobre</Label>
                  <Select
                    value={formData.transfer_fee_applies_to}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, transfer_fee_applies_to: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="capital">Solo capital</SelectItem>
                      <SelectItem value="fees">Solo honorarios</SelectItem>
                      <SelectItem value="both">Capital + honorarios</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Refinancing Section */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-blue-600" />
                  Habilitar refinanciamiento
                </p>
                <p className="text-xs text-slate-500">Permitir a clientes solicitar refinanciamiento</p>
              </div>
              <Switch
                checked={formData.enable_refinancing}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_refinancing: checked }))}
              />
            </div>

            {/* Payment Plans Section */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-amber-600" />
                  Pago parcial por acta
                </p>
                <p className="text-xs text-slate-500">Permitir que clientes elijan qué actas pagar individualmente</p>
              </div>
              <Switch
                checked={formData.allow_partial_acta_payment}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_partial_acta_payment: checked }))}
              />
            </div>

            {/* Payment Plans Section - Installments */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-teal-600" />
                  Habilitar planes de pago
                </p>
                <p className="text-xs text-slate-500">Permitir pago en cuotas</p>
              </div>
              <Switch
                checked={formData.enable_payment_plans}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_payment_plans: checked }))}
              />
            </div>

            {formData.enable_payment_plans && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-teal-50 rounded-lg border border-teal-100">
                <div>
                  <Label>Máximo de cuotas</Label>
                  <Input
                    type="number"
                    value={formData.max_installments}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_installments: e.target.value }))}
                    placeholder="6"
                    min="2"
                    max="24"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label>Interés por cuota (%)</Label>
                  <Input
                    type="number"
                    value={formData.installment_interest_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, installment_interest_rate: e.target.value }))}
                    placeholder="0"
                    min="0"
                    step="0.5"
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Payment Methods Override Section */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-indigo-600" />
                  Configuración de medios de pago
                </p>
                <p className="text-xs text-slate-500">Usar configuración específica para esta cartera</p>
              </div>
              <Switch
                checked={formData.override_payment_methods}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, override_payment_methods: checked }))}
              />
            </div>

            {formData.override_payment_methods && (
              <div className="space-y-3 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <p className="text-sm font-medium text-indigo-800 mb-3">Medios de pago habilitados:</p>
                
                <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium">Mercado Pago</span>
                  </div>
                  <Switch
                    checked={formData.payment_methods?.mercadopago}
                    onCheckedChange={(checked) => setFormData(prev => ({ 
                      ...prev, 
                      payment_methods: { ...prev.payment_methods, mercadopago: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-sm font-medium">Transferencia</span>
                  </div>
                  <Switch
                    checked={formData.payment_methods?.transfer}
                    onCheckedChange={(checked) => setFormData(prev => ({ 
                      ...prev, 
                      payment_methods: { ...prev.payment_methods, transfer: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg opacity-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-violet-100 rounded flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-violet-600" />
                    </div>
                    <span className="text-sm font-medium">Modo</span>
                    <Badge className="bg-amber-100 text-amber-700 text-xs">Próximamente</Badge>
                  </div>
                  <Switch checked={false} disabled />
                </div>

                <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg opacity-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="text-sm font-medium">Pago Fácil / Rapipago</span>
                    <Badge className="bg-amber-100 text-amber-700 text-xs">Próximamente</Badge>
                  </div>
                  <Switch checked={false} disabled />
                </div>

                <p className="text-xs text-indigo-600 mt-2">
                  Si no se configura, se usará la configuración global de la empresa.
                </p>
              </div>
            )}

            {/* Reminders Section */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-green-600" />
                  Recordatorios automáticos
                </p>
                <p className="text-xs text-slate-500">Enviar recordatorios por WhatsApp</p>
              </div>
              <Switch
                checked={formData.enable_reminders}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_reminders: checked }))}
              />
            </div>

            {formData.enable_reminders && (
              <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-100">
                <div>
                  <Label>Plantilla de mensaje</Label>
                  <textarea
                    value={formData.reminder_config?.message_template || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      reminder_config: { ...prev.reminder_config, message_template: e.target.value }
                    }))}
                    className="w-full mt-1 p-3 rounded-lg border border-green-200 text-sm min-h-[100px] resize-none"
                    placeholder="Hola {nombre}, te recordamos que tienes una deuda de {deuda}..."
                  />
                  <p className="text-xs text-green-700 mt-2">
                    Variables disponibles: <code className="bg-green-100 px-1 rounded">{'{nombre}'}</code>, <code className="bg-green-100 px-1 rounded">{'{deuda}'}</code>, <code className="bg-green-100 px-1 rounded">{'{link}'}</code>, <code className="bg-green-100 px-1 rounded">{'{cartera}'}</code>
                  </p>
                </div>
              </div>
            )}

            {/* Document Requests Section */}
            <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Solicitud de Actas/Facturas</p>
                  <p className="text-sm text-blue-700">Permitir que clientes soliciten documentos faltantes</p>
                </div>
              </div>
              <Switch
                checked={formData.enable_document_requests}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_document_requests: checked }))}
              />
            </div>
            
            {formData.enable_document_requests && (
              <div className="bg-blue-50/50 p-4 rounded-lg space-y-4 border border-blue-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email del Responsable</Label>
                    <Input
                      type="email"
                      value={formData.document_request_config?.responsible_email || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        document_request_config: { ...prev.document_request_config, responsible_email: e.target.value }
                      }))}
                      placeholder="responsable@empresa.com"
                    />
                  </div>
                  <div>
                    <Label>Teléfono del Responsable (WhatsApp)</Label>
                    <Input
                      value={formData.document_request_config?.responsible_phone || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        document_request_config: { ...prev.document_request_config, responsible_phone: e.target.value }
                      }))}
                      placeholder="+54 11 1234-5678"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between bg-white p-3 rounded border">
                  <div>
                    <p className="font-medium text-sm">Notificar por WhatsApp</p>
                    <p className="text-xs text-slate-600">Enviar mensaje al responsable cuando hay una nueva solicitud</p>
                  </div>
                  <Switch
                    checked={formData.document_request_config?.notify_whatsapp || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ 
                      ...prev, 
                      document_request_config: { ...prev.document_request_config, notify_whatsapp: checked }
                    }))}
                  />
                </div>
                
                <div>
                  <Label>Tiempo de respuesta prometido (horas)</Label>
                  <Select
                    value={String(formData.document_request_config?.response_time_hours || 48)}
                    onValueChange={(v) => setFormData(prev => ({ 
                      ...prev, 
                      document_request_config: { ...prev.document_request_config, response_time_hours: parseInt(v) }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">24 horas</SelectItem>
                      <SelectItem value="48">48 horas</SelectItem>
                      <SelectItem value="72">72 horas</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    El cliente verá este tiempo como estimado de respuesta
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleUpdate}
                disabled={submitting || !formData.name.trim()}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="update-portfolio-submit"
              >
                {submitting ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Modal */}
      <Dialog open={showDocumentsModal} onOpenChange={setShowDocumentsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPortfolio?.portfolio_type === 'servicio' ? 'Facturas' : 'Actas'} - {editingPortfolio?.name}
            </DialogTitle>
            <DialogDescription>
              Sube documentos para vincular con clientes. El nombre del archivo (sin extensión) debe coincidir con el campo "{LINK_FIELDS.find(f => f.value === editingPortfolio?.document_link_field)?.label || 'configurado'}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload Section */}
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                ref={docInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf"
                className="hidden"
                onChange={handleDocumentsUpload}
              />
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <Button
                onClick={() => docInputRef.current?.click()}
                disabled={uploadingDocs}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {uploadingDocs ? 'Subiendo...' : 'Seleccionar Archivos'}
              </Button>
              <p className="text-xs text-slate-500 mt-2">
                JPG, PNG, WEBP o PDF (máx. 5MB por archivo)
              </p>
            </div>

            {/* Documents List */}
            <div>
              <h4 className="font-medium text-slate-900 mb-2">
                Documentos Cargados ({documents.length})
              </h4>
              
              {documents.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  No hay documentos cargados
                </p>
              ) : (
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {documents.map((doc) => (
                    <div key={doc.link_value} className="flex items-center justify-between p-3 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{doc.filename}</p>
                          <p className="text-xs text-slate-500">
                            Vinculado a: {editingPortfolio?.document_link_field} = {doc.link_value}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteDocument(doc.link_value)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaigns Modal */}
      <Dialog open={!!showCampaignsModal} onOpenChange={() => setShowCampaignsModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-600" />
              Gestión de Campañas
            </DialogTitle>
          </DialogHeader>
          {showCampaignsModal && (
            <CampaignManager
              portfolioId={showCampaignsModal.id}
              portfolioName={showCampaignsModal.name}
              onClose={() => setShowCampaignsModal(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioManager;
